import { Directive, OnInit, ViewChild, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import { EntityStatus, FileAdminFilterKey, FilePurpose, FileSummaryDto } from '@workspace/data-types';
import {
  ActionButton,
  ActionMenu,
  BaseDataTableComponent,
  DataHeaderFilter,
  DataListColumn,
} from '@workspace/ng-aura';
import { filePurposeDisplayTransform, stateStatusDisplayTransform } from '@workspace/ng-data-access';
import { Observable } from 'rxjs';

export type FileColumnKey =
  'filename' | 'mimeType' | 'size' | 'purpose' | 'uploadedBy' | 'updatedAt' | 'createdAt' | 'state.status';

@Directive()
export abstract class AbstractFilesDataTableCoreComponent<T extends FileSummaryDto>
  extends BaseDataTableComponent<T, FileColumnKey>
  implements OnInit
{
  override topMenu = signal<ActionMenu | null>(null);
  override columns = signal<DataListColumn<FileColumnKey>[]>([
    { key: 'uploadedBy', label: 'Téléversé par', locked: true, display: true },
    { key: 'filename', label: 'Nom', display: true, locked: true },
    { key: 'size', label: 'Taille', display: true, locked: true },
    { key: 'state.status', label: 'Status', display: true, locked: true },
    { key: 'mimeType', label: 'Mimetype', display: true },
    { key: 'purpose', label: 'Usage', display: true },
    { key: 'createdAt', label: 'Date de création', display: false },
    { key: 'updatedAt', label: 'Date de modification', display: false },
  ]);
  override topButtons = signal<ActionButton[]>([]);
  override itemActions = signal<ActionButton[]>([
    {
      label: 'Ouvrir',
      icon: 'open_in_new',
      onClick: (file: FileSummaryDto) => this.navigateToFile(file),
      disabled: false,
      color: 'primary',
    },
  ]);
  override filters = signal<DataHeaderFilter<FileAdminFilterKey>[]>([
    {
      label: 'State',
      column: FileAdminFilterKey.State,
    },
    {
      label: 'Mimetype',
      column: FileAdminFilterKey.Mimetype,
    },
    {
      label: 'Usage',
      column: FileAdminFilterKey.Purpose,
    },
  ]);

  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const purpose = params.getAll(FileAdminFilterKey.Purpose);
      const mimeType = params.getAll(FileAdminFilterKey.Mimetype);
      const state = params.getAll(FileAdminFilterKey.State);
      this.filterChanged({
        changed: true,
        filter: {
          purpose: purpose.length ? purpose : [],
          mimeType: mimeType.length ? mimeType : [],
          state: state.length ? state : [],
        },
      });
    });
  }

  navigateToFile(file: FileSummaryDto): void {
    void this.router.navigate(['/files', file._id]);
  }

  abstract override getAll(): Observable<T[]>;
  abstract override countAll(): Observable<number>;
  override getDisplayForEntry(key: FileAdminFilterKey, rawValue: string): string {
    switch (key) {
      case FileAdminFilterKey.Purpose:
        return filePurposeDisplayTransform(rawValue as FilePurpose);
      case FileAdminFilterKey.State:
        return stateStatusDisplayTransform(rawValue as EntityStatus);
      case FileAdminFilterKey.Mimetype:
        return rawValue;
      default:
        return key;
    }
  }
}
